import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const roleLabel = {
  "tour-owner": "Tour Owner",
  "tour-distributor": "Distributor",
};

const roleColors = {
  "tour-owner": { bg: "#FFF0F0", color: "#E50000", border: "#FECACA" },
  "tour-distributor": { bg: "#EFF6FF", color: "#3B82F6", border: "#BFDBFE" },
};

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: "#FFFFFF",
      borderRadius: 3,
      border: "1px solid #EAECF0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionHeader = ({ title, subtitle }) => (
  <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: "#111827" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: "0.8rem", color: "#6B7280", mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [profileStatus, setProfileStatus] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("authUser");
    const role = localStorage.getItem("userRole");
    if (stored) {
      const parsed = JSON.parse(stored);
      const u = { ...parsed, role };
      setUser(u);
      setProfileForm({
        firstName: parsed.firstName ?? "",
        lastName: parsed.lastName ?? "",
        email: parsed.email ?? "",
        phone: parsed.phone ?? "",
      });
    }
  }, []);

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "?";

  const roleMeta = roleColors[user?.role];

  const joinedDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : null;

  const handleProfileChange = (e) =>
    setProfileForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handlePasswordChange = (e) =>
    setPasswordForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileStatus(null);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/users/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phone,
        }),
      });
      if (!res.ok) throw new Error("Failed to update profile");
      const updated = await res.json();
      const stored = JSON.parse(localStorage.getItem("authUser") || "{}");
      localStorage.setItem(
        "authUser",
        JSON.stringify({ ...stored, ...updated })
      );
      setUser((u) => ({ ...u, ...updated }));
      setProfileStatus({ type: "success", message: "Profile updated successfully." });
    } catch {
      setProfileStatus({
        type: "error",
        message: "Could not save changes. Please try again.",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: "error", message: "New passwords do not match." });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordStatus({
        type: "error",
        message: "Password must be at least 8 characters.",
      });
      return;
    }
    setPasswordLoading(true);
    setPasswordStatus(null);
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to change password");
      }
      setPasswordStatus({ type: "success", message: "Password changed successfully." });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordStatus({
        type: "error",
        message: err.message || "Could not update password. Please try again.",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleVisibility = (field) =>
    setShowPasswords((p) => ({ ...p, [field]: !p[field] }));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Grid container spacing={3}>
        {/* Left column — profile card */}
        <Grid item xs={12} md={4}>
          <Card>
            {/* Avatar section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #111827 0%, #1F2937 100%)",
                borderRadius: "12px 12px 0 0",
                pt: 4,
                pb: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: -5,
                px: 3,
                pb: 3,
              }}
            >
              <Avatar
                src={user?.avatarUrl}
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: "1.75rem",
                  fontWeight: 800,
                  bgcolor: "#E50000",
                  color: "#fff",
                  border: "4px solid #fff",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  mb: 1.5,
                }}
              >
                {initials}
              </Avatar>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#111827" }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
              {user?.role && roleMeta && (
                <Box
                  sx={{
                    mt: 0.75,
                    px: 1.25,
                    py: 0.35,
                    borderRadius: 1.5,
                    bgcolor: roleMeta.bg,
                    color: roleMeta.color,
                    border: `1px solid ${roleMeta.border}`,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                  }}
                >
                  {roleLabel[user.role] ?? user.role}
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: "#F3F4F6" }} />

            {/* Info rows */}
            <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <EmailOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF", mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Email
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.84rem", color: "#374151", fontWeight: 500, wordBreak: "break-all" }}
                  >
                    {user?.email}
                  </Typography>
                </Box>
              </Box>

              {user?.phone && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF", mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontSize: "0.68rem", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Phone
                    </Typography>
                    <Typography sx={{ fontSize: "0.84rem", color: "#374151", fontWeight: 500 }}>
                      {user.phone}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <WorkOutlineIcon sx={{ fontSize: 17, color: "#9CA3AF", mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Role
                  </Typography>
                  <Typography sx={{ fontSize: "0.84rem", color: "#374151", fontWeight: 500 }}>
                    {roleLabel[user?.role] ?? user?.role ?? "—"}
                  </Typography>
                </Box>
              </Box>

              {joinedDate && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF", mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontSize: "0.68rem", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Member Since
                    </Typography>
                    <Typography sx={{ fontSize: "0.84rem", color: "#374151", fontWeight: 500 }}>
                      {joinedDate}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Right column — forms */}
        <Grid item xs={12} md={8}>
          {/* Edit profile */}
          <Card sx={{ mb: 3 }}>
            <SectionHeader
              title="Personal Information"
              subtitle="Update your name and contact details."
            />
            <Divider sx={{ borderColor: "#F3F4F6" }} />

            <Box
              component="form"
              onSubmit={handleProfileSubmit}
              sx={{ px: 3, pt: 2.5, pb: 3 }}
            >
              {profileStatus && (
                <Alert
                  severity={profileStatus.type}
                  icon={
                    profileStatus.type === "success" ? (
                      <CheckCircleOutlineIcon fontSize="small" />
                    ) : undefined
                  }
                  sx={{ mb: 2.5, borderRadius: 2, fontSize: "0.84rem" }}
                >
                  {profileStatus.message}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    name="firstName"
                    value={profileForm.firstName}
                    onChange={handleProfileChange}
                    fullWidth
                    required
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    name="lastName"
                    value={profileForm.lastName}
                    onChange={handleProfileChange}
                    fullWidth
                    required
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email Address"
                    name="email"
                    value={profileForm.email}
                    fullWidth
                    size="small"
                    disabled
                    helperText="Email cannot be changed here."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    name="phone"
                    value={profileForm.phone}
                    onChange={handleProfileChange}
                    fullWidth
                    size="small"
                    placeholder="+1 234 567 8900"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={profileLoading}
                  sx={{
                    bgcolor: "#111827",
                    color: "#fff",
                    borderRadius: 1.5,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.84rem",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#1F2937", boxShadow: "none" },
                    "&:disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
                  }}
                >
                  {profileLoading ? (
                    <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                  ) : null}
                  Save Changes
                </Button>
              </Box>
            </Box>
          </Card>

          {/* Change password */}
          <Card>
            <SectionHeader
              title="Security"
              subtitle="Update your password to keep your account secure."
            />
            <Divider sx={{ borderColor: "#F3F4F6" }} />

            <Box
              component="form"
              onSubmit={handlePasswordSubmit}
              sx={{ px: 3, pt: 2.5, pb: 3 }}
            >
              {passwordStatus && (
                <Alert
                  severity={passwordStatus.type}
                  icon={
                    passwordStatus.type === "success" ? (
                      <CheckCircleOutlineIcon fontSize="small" />
                    ) : undefined
                  }
                  sx={{ mb: 2.5, borderRadius: 2, fontSize: "0.84rem" }}
                >
                  {passwordStatus.message}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Current Password"
                    name="currentPassword"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("current")}
                            edge="end"
                          >
                            {showPasswords.current ? (
                              <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="New Password"
                    name="newPassword"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    size="small"
                    helperText="Minimum 8 characters."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("new")}
                            edge="end"
                          >
                            {showPasswords.new ? (
                              <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("confirm")}
                            edge="end"
                          >
                            {showPasswords.confirm ? (
                              <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={passwordLoading}
                  sx={{
                    bgcolor: "#E50000",
                    color: "#fff",
                    borderRadius: 1.5,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.84rem",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#C40000", boxShadow: "none" },
                    "&:disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
                  }}
                >
                  {passwordLoading ? (
                    <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                  ) : null}
                  Update Password
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
